import {
  useGetBillOfMaterialSummariesForNodes,
  useGetBillOfMaterialSummary
} from "../../../../../api/billOfMaterialSummary";
import { useGetProductSummary } from "../../../../../api/productSummary";
import { EnvironmentalFootprintCards } from "../../../../../components/environmentalFootprint";
import { Skeleton } from "../../../../../components/ui/common/skeleton";
import { NOT_APPLICABLE } from "../../../../../config/constants";
import { IngredientDataType } from "../../../../../config/enums/IngredientType.enum";
import { ROLES } from "../../../../../config/permission-maps";
import { getUserRolesInLocal } from "../../../../../utils/cacheStorage";
import { humanize } from "../../../../../utils/utils";
import { ProcessingImpactsCard } from "../../../../productSummary/components";
import { formatCalculatedDate, processLifecycleStages } from "../../../../productSummary/helpers";
import { useProcessingSteps } from "../../../../productSummary/hooks/useProductSummary";
import { ImpactData } from "../../../../productSummary/types";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";
import FootprintHeader from "../FootprintHeader";

interface EnvironmentalFootprintTabProps {
  ingredientDetails: IngredientRowViewModel | undefined;
}

/**
 * "Environmental Footprint" tab — a `FootprintHeader` (reference flow + basis
 * pill) above the shared `EnvironmentalFootprintCards` layout
 * (src/components/environmentalFootprint/), the same one used by the product
 * details tab and the `ProductSummary` page. `isAverageProduct={false}` so
 * the "Calculated on" block shows, using `supplierIngredientDetails.climate_impact_date`
 * — a pipeline-computed field on the ingredient row itself (see
 * `suppliersLookup` in the backend) rather than anything off the summary
 * response, so it's available for both the SUPPLIER and BOM data-type paths.
 */
const EnvironmentalFootprintTab = ({ ingredientDetails }: EnvironmentalFootprintTabProps) => {
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const details = ingredientDetails?.supplierIngredientDetails as
    | Record<string, unknown>
    | null
    | undefined;
  const isSupplier = details?.data_type === IngredientDataType.SUPPLIER;

  const ingredientId = ingredientDetails?.ingredient_id;
  const supplierId = ingredientDetails?.supplier_id || undefined;
  const productSummaryRef = details?.product_summary as
    | { productId?: string; salesUnitId?: string }
    | undefined;

  const productSummaryData = useGetProductSummary({
    productId: productSummaryRef?.productId,
    salesUnitId: productSummaryRef?.salesUnitId,
    isSupplier
  });

  const billOfMaterialSummaryData = useGetBillOfMaterialSummary({
    ingredientId,
    supplierId,
    isSupplier
  });

  // Only the BOM path can resolve a processing-steps chart today — the
  // supplier-type (product-summary-backed) path has no summary id available
  // here, and the product node-summary-impacts endpoint requires one.
  const { data: billOfMaterialSummariesForNode } = useGetBillOfMaterialSummariesForNodes({
    ingredientId: isSupplier ? undefined : ingredientId,
    supplierId: isSupplier ? undefined : supplierId
  });
  const processingSteps = useProcessingSteps(billOfMaterialSummariesForNode);

  const productSummary = isSupplier ? productSummaryData.data : billOfMaterialSummaryData.data;
  const isLoading = isSupplier
    ? productSummaryData.isFetching
    : billOfMaterialSummaryData.isFetching;

  if (isLoading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Skeleton className="tw-h-40 tw-w-full" />
        <Skeleton className="tw-h-64 tw-w-full" />
      </div>
    );
  }

  if (checkIsUserSupplier) {
    return (
      <p className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-800">
        Calculated: Upgrade to see results
      </p>
    );
  }

  if (!productSummary) {
    return (
      <p className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-800">
        Data collection in progress
      </p>
    );
  }

  const { measure: climateChangeMeasure, unit: climateChangeUnit } =
    productSummary?.climateChange || {};
  const cycleStageData = productSummary?.cycleStages || {};
  const totalImpact = (productSummary?.totalImpact || []) as unknown as ImpactData[];
  const lifecycleStages = processLifecycleStages(cycleStageData, NOT_APPLICABLE, humanize);
  const calculatedDate = formatCalculatedDate(details?.climate_impact_date as string | undefined);

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <FootprintHeader ingredientDetails={ingredientDetails} />
      <EnvironmentalFootprintCards
        productImage={ingredientDetails?.ingredient_image_path}
        productName={ingredientDetails?.ingredient_name}
        productSubtitle={ingredientDetails?.supplier_name}
        calculatedDate={calculatedDate}
        isAverageProduct={false}
        climateChangeMeasure={climateChangeMeasure}
        climateChangeUnit={climateChangeUnit}
        basisLabel="ingredient"
        lifecycleStages={lifecycleStages}
        totalImpact={totalImpact}
        compact
        wrapImpactGrid
        middleContent={
          processingSteps.length > 0 ? (
            <ProcessingImpactsCard processingSteps={processingSteps} />
          ) : undefined
        }
      />
    </div>
  );
};

export default EnvironmentalFootprintTab;
